// src/features/authz/BatchCheckPanel.tsx
// Multiple authorization checks in one request (max 50) — one line per check,
// "user relation object" tuple syntax.
// See docs/ux/authz-ia.md section 2.

import { useState } from 'react';
import { useI18n, useAuthz } from '@react/hooks';
import { Button, Textarea, ErrorAlert, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '@react/components';
import type { BatchCheckResult } from '@core/types/authz';
import { parseTupleString } from './logic';

export function BatchCheckPanel() {
  const { t } = useI18n();
  const store = useAuthz();
  const [text, setText] = useState('user:bob viewer document:readme\nuser:alice editor document:readme');
  const [results, setResults] = useState<BatchCheckResult[] | null>(null);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
    const errors: string[] = [];
    const items = lines.map((line, i) => {
      const parsed = parseTupleString(line);
      if (!parsed) {
        errors.push(t('authz.batchCheck.lineError', { line: i + 1 }));
        return null;
      }
      return { tuple_key: parsed, correlation_id: String(i) };
    }).filter((v): v is { tuple_key: { user: string; relation: string; object: string }; correlation_id: string } => v !== null);

    setParseErrors(errors);
    if (errors.length > 0 || items.length === 0) return;

    setIsRunning(true);
    setResults(null);
    try {
      const r = await store.BatchCheck({ checks: { items }, authorization_model_id: '' });
      setResults(r.results?.items ?? []);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="space-y-2">
        <Textarea
          label={t('authz.batchCheck.field')}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          className="font-mono text-xs"
          helperText={t('authz.batchCheck.helper')}
        />
        {parseErrors.map((e) => <p key={e} className="text-sm text-destructive">{e}</p>)}
        <Button type="submit" size="sm" disabled={isRunning}>{isRunning ? t('common.loading') : t('authz.batchCheck.run')}</Button>
      </form>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {results && (
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('authz.batchCheck.col.check')}</TableHead>
              <TableHead>{t('authz.batchCheck.col.result')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {results.length === 0 ? (
              <TableEmpty colSpan={2} message={t('authz.batchCheck.empty')} />
            ) : (
              results.map((r) => (
                <TableRow key={r.correlation_id}>
                  <TableCell className="font-mono text-xs">
                    {text.split('\n').filter(Boolean)[parseInt(r.correlation_id, 10)] ?? r.correlation_id}
                  </TableCell>
                  <TableCell>
                    {r.error ? (
                      <span className="text-destructive">{r.error}</span>
                    ) : (
                      <span className={r.response?.allowed ? 'text-success' : 'text-destructive'}>
                        {r.response?.allowed ? t('authz.check.allowed') : t('authz.check.denied')}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
